import React, { useEffect, useRef, useState } from "react";

import WappIcono from "../../../components/wappIcono";

/**
 * Visor de fotos de WappDrive (Esteban, 2026-10-13: "no hay visor de imágenes en WappDrive"): acercar con la rueda, con
 * los botones, con doble clic o con dos dedos; mover arrastrando cuando está acercada; girar; y pasar a la foto anterior o
 * siguiente de la carpeta con ← → (o deslizando en el celular). La foto nunca sale de WAPP.
 */
const MIN = 1, MAX = 8;
const limitar = (z: number) => Math.min(MAX, Math.max(MIN, z));

const VisorImagen: React.FC<{ url: string; nombre: string; onAnterior?: () => void; onSiguiente?: () => void; posicion?: string }> =
	({ url, nombre, onAnterior, onSiguiente, posicion }) => {
	const [zoom, setZoom] = useState(1);
	const [giro, setGiro] = useState(0);
	const [mov, setMov] = useState({ x: 0, y: 0 });
	const [error, setError] = useState(false);
	const [cargada, setCargada] = useState(false);
	const caja = useRef<HTMLDivElement>(null);
	const arrastre = useRef<{ x: number; y: number; mx: number; my: number } | null>(null);
	const dedos = useRef<{ d: number; z: number } | null>(null);
	const toque = useRef<{ x: number; t: number } | null>(null);

	// Otra foto: todo de vuelta a como estaba.
	useEffect(() => { setZoom(1); setGiro(0); setMov({ x: 0, y: 0 }); setError(false); setCargada(false); }, [url]);
	useEffect(() => { if (zoom === 1) setMov({ x: 0, y: 0 }); }, [zoom]);

	// Flechas para pasar de foto, +/- para acercar (Escape lo cierra quien abrió el visor).
	useEffect(() => {
		const tecla = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft" && onAnterior) { e.preventDefault(); onAnterior(); }
			else if (e.key === "ArrowRight" && onSiguiente) { e.preventDefault(); onSiguiente(); }
			else if (e.key === "+" || e.key === "=") setZoom((z) => limitar(z * 1.25));
			else if (e.key === "-") setZoom((z) => limitar(z / 1.25));
		};
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [onAnterior, onSiguiente]);

	// Rueda: acercar sin mover la página (el listener pasivo de React no deja preventDefault).
	useEffect(() => {
		const el = caja.current;
		if (!el) return;
		const rueda = (e: WheelEvent) => { e.preventDefault(); setZoom((z) => limitar(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))); };
		el.addEventListener("wheel", rueda, { passive: false });
		return () => el.removeEventListener("wheel", rueda);
	}, []);

	const abajo = (e: React.PointerEvent) => {
		if (zoom === 1 || e.pointerType === "touch") return;
		arrastre.current = { x: e.clientX, y: e.clientY, mx: mov.x, my: mov.y };
		(e.target as HTMLElement).setPointerCapture?.(e.pointerId);
	};
	const mover = (e: React.PointerEvent) => {
		const a = arrastre.current;
		if (a) setMov({ x: a.mx + e.clientX - a.x, y: a.my + e.clientY - a.y });
	};
	const arriba = () => { arrastre.current = null; };

	const distancia = (t: React.TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
	const toqueInicio = (e: React.TouchEvent) => {
		if (e.touches.length === 2) { dedos.current = { d: distancia(e.touches), z: zoom }; return; }
		const t = e.touches[0];
		toque.current = { x: t.clientX, t: Date.now() };
		if (zoom > 1) arrastre.current = { x: t.clientX, y: t.clientY, mx: mov.x, my: mov.y };
	};
	const toqueMueve = (e: React.TouchEvent) => {
		if (e.touches.length === 2 && dedos.current) { setZoom(limitar(dedos.current.z * distancia(e.touches) / dedos.current.d)); return; }
		const a = arrastre.current, t = e.touches[0];
		if (a && t) setMov({ x: a.mx + t.clientX - a.x, y: a.my + t.clientY - a.y });
	};
	const toqueFin = (e: React.TouchEvent) => {
		dedos.current = null; arrastre.current = null;
		const ini = toque.current, t = e.changedTouches[0];
		toque.current = null;
		// Deslizar con la foto sin acercar: anterior / siguiente.
		if (ini && t && zoom === 1 && Date.now() - ini.t < 600 && Math.abs(t.clientX - ini.x) > 60) {
			if (t.clientX < ini.x) onSiguiente?.(); else onAnterior?.();
		}
	};

	return (
		<div className="wd-foto" onClick={(e) => e.stopPropagation()}>
			<div ref={caja} className={"wd-foto-lienzo" + (zoom > 1 ? " acercada" : "")} onPointerDown={abajo} onPointerMove={mover} onPointerUp={arriba} onPointerCancel={arriba}
				onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2.5))} onTouchStart={toqueInicio} onTouchMove={toqueMueve} onTouchEnd={toqueFin}
				onContextMenu={(e) => e.preventDefault()}>
				{error ? (
					<div className="wd-sinvista">El navegador no puede enseñar esta foto{/\.hei[cf]$/i.test(nombre) ? " (formato HEIC del iPhone)" : ""}.</div>
				) : (
					<>
						{!cargada ? <div className="wd-foto-cargando">Cargando…</div> : null}
						<img src={url} alt={nombre} draggable={false} onLoad={() => setCargada(true)} onError={() => setError(true)}
							style={{ transform: `translate(${mov.x}px, ${mov.y}px) scale(${zoom}) rotate(${giro}deg)`, opacity: cargada ? 1 : 0 }} />
					</>
				)}
				{onAnterior ? <button type="button" className="wd-foto-flecha izq" onClick={onAnterior} aria-label="Foto anterior"><WappIcono n="back" /></button> : null}
				{onSiguiente ? <button type="button" className="wd-foto-flecha der" onClick={onSiguiente} aria-label="Foto siguiente"><WappIcono n="back" /></button> : null}
			</div>
			{!error ? (
				<div className="wd-foto-barra">
					<button type="button" onClick={() => setZoom((z) => limitar(z / 1.25))} disabled={zoom <= MIN} aria-label="Alejar" title="Alejar"><WappIcono n="minus" /></button>
					<button type="button" className="wd-foto-pct" onClick={() => setZoom(1)} title="Tamaño original">{Math.round(zoom * 100)}%</button>
					<button type="button" onClick={() => setZoom((z) => limitar(z * 1.25))} disabled={zoom >= MAX} aria-label="Acercar" title="Acercar"><WappIcono n="plus" /></button>
					<button type="button" onClick={() => setGiro((g) => (g + 90) % 360)} aria-label="Girar" title="Girar"><span className="wd-foto-giro" aria-hidden="true">↻</span></button>
					{posicion ? <span className="wd-foto-pos">{posicion}</span> : null}
				</div>
			) : null}
		</div>
	);
};

export default VisorImagen;
